import './AppIcon'

// Types are adopted by Rails flashes https://edgeapi.rubyonrails.org/classes/ActionDispatch/Flash.html
const iconTypes = {
  notice: 'info',
  alert: 'dangerous',
} as const

const alertTypes = {
  notice: 'info',
  alert: 'error',
} as const

type AlertType = keyof typeof alertTypes

export class AppAlert extends HTMLElement {
  static observedAttributes = ['title', 'type']

  #el: {
    container?: HTMLDivElement
    icon?: HTMLElement
    title?: HTMLElement
    messageSlot?: HTMLSlotElement
  } = {}
  #initialized = false

  connectedCallback() {
    if (!this.#initialized) {
      this.#render()
      this.#initialized = true
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue || !this.#initialized) return

    switch (name) {
      case 'type':
        this.#updateType(newValue as AlertType)
        break
      case 'title':
        this.#updateTitle(newValue)
        break
    }
  }

  #updateType(type: AlertType) {
    if (!this.#el.container || !this.#el.icon) return

    // Remove old alert type classes
    Object.values(alertTypes).forEach((className) => {
      this.#el.container?.classList.remove(`alert-${className}`)
    })

    // Add new alert type class
    if (alertTypes[type]) {
      this.#el.container.classList.add(`alert-${alertTypes[type]}`)
    }

    // Update icon
    this.#el.icon.setAttribute('name', iconTypes[type] || 'info')
  }

  #updateTitle(title: string | null) {
    if (!this.#el.title) return

    if (title) {
      this.#el.title.textContent = title
      this.#el.title.style.display = ''
    } else {
      this.#el.title.style.display = 'none'
    }
  }

  #render() {
    const type = (this.getAttribute('type') as AlertType) || 'notice'
    const title = this.getAttribute('title')

    // Store original slotted content
    const slottedContent = Array.from(this.querySelectorAll('[slot="message"]'))
    //
    // Create structure in Light DOM
    this.innerHTML = `
      <div class="alert alert-soft alert-${alertTypes[type]}" role="alert">
        <app-icon name="${iconTypes[type] || 'info'}"></app-icon>
        <div class="flex flex-col gap-2">
          <strong style="${title ? '' : 'display: none;'}"></strong>
          <div data-slot="message"></div>
        </div>
      </div>
    `

    // Assign element references
    this.#el.container = this.querySelector('.alert') as HTMLDivElement
    this.#el.icon = this.querySelector('app-icon') as HTMLElement
    this.#el.title = this.querySelector('div > strong') as HTMLElement
    const messageContent = this.querySelector('[data-slot="message"]') as HTMLElement

    this.#updateTitle(title)

    // Re-append slotted content to message area
    slottedContent.forEach((node) => {
      node.removeAttribute('slot')
      messageContent.appendChild(node)
    })
  }
}

customElements.define('app-alert', AppAlert)
